"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LayupValidator = void 0;
const locale_1 = require("../utils/homeport/locale");
const base_1 = require("./base");
const validators_1 = require("./validators");
const date_fns_1 = require("date-fns");
const LayupValidator = (question) => {
    const { listQuestions, listSecondQuestions } = question.metadata;
    const internalValidators = listQuestions.reduce((map, prop) => {
        const validator = validators_1.ValidatorsFactory.createFromRaw(prop);
        map[prop.property] = validator;
        return map;
    }, {});
    const entryValidator = new base_1.BaseValidator();
    Object.keys(internalValidators).forEach(key => {
        entryValidator.addRule(item => {
            const propValidator = internalValidators[key];
            if (item)
                propValidator.validate(item[key]);
            return propValidator.errors.length ? propValidator.errors[0] : undefined;
        });
    });
    const internalSecondValidators = listSecondQuestions.reduce((map, prop) => {
        const validator = validators_1.ValidatorsFactory.createFromRaw(prop);
        map[prop.property] = validator;
        return map;
    }, {});
    const entrySecondValidator = new base_1.BaseValidator();
    Object.keys(internalSecondValidators).forEach(key => {
        entrySecondValidator.addRule(item => {
            const propValidator = internalSecondValidators[key];
            propValidator.validate(item[key]);
            return propValidator.errors.length ? propValidator.errors[0] : undefined;
        });
    });
    //dynamic validation, meaning taking into account 2 question values
    const validatorFunction = {
        desc: 'minTwoMonthsDifference',
        errTitle: 'E_Layup',
        validate: (listObj) => {
            if (!listObj)
                return false;
            const isFirstList = Object.keys(listObj)?.includes(listQuestions[0].property);
            const fromQuestionValue = isFirstList ? listObj[listQuestions[0].property] : listObj[listSecondQuestions[0].property];
            const toQuestionValue = isFirstList ? listObj[listQuestions[1].property] : listObj[listSecondQuestions[1].property];
            if (fromQuestionValue && toQuestionValue) {
                const from = new Date(fromQuestionValue);
                const to = new Date(toQuestionValue);
                const differenceInMonths = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
                // returning true means no error
                return differenceInMonths >= 2;
            }
            return false;
        },
    };
    //dynamic validation, meaning taking into account 2 question values
    const validatorFunctionTotal = {
        desc: 'minTwoMonthsDifference',
        errTitle: 'E_Layup_Total',
        validate: ({ list }) => {
            if (!list)
                return false;
            const fromQuestionValue1 = list[0][listQuestions[0].property];
            const fromQuestionValue2 = list[1][listSecondQuestions[0].property];
            const toQuestionValue1 = list[0][listQuestions[1].property];
            const toQuestionValue2 = list[1][listSecondQuestions[1].property];
            if (fromQuestionValue1 && toQuestionValue1 && fromQuestionValue2 && toQuestionValue2) {
                const effectiveDate = new Date();
                const questionsObj = {
                    Layup1_From: fromQuestionValue1,
                    Layup1_To: toQuestionValue1,
                    Layup2_From: fromQuestionValue2,
                    Layup2_To: toQuestionValue2,
                };
                const layupMonths = calculateLayupMonths({ effectiveDate, questionsObj });
                // returning true means no error
                return layupMonths >= 2;
            }
            return false;
        },
    };
    /**
      * Calculates the total number of boat layup months included within a policy period, taking into account
      * any overlapping days between two layup periods to avoid double counting. The function checks for the
      * existence of layup periods (Layup1 and Layup2), calculates their durations within a one-year policy
      * period starting from the Effective_Date, and ensures that overlapping days are only counted once.
      * It validates that each layup period has both a start and an end date. If a layup start date is
      * provided without a corresponding end date, the function throws an error. The total layup duration
      * is then converted to months, with any partial month being rounded down to the nearest whole month.
      * same as the function used in rate function
      */
    const calculateLayupMonths = ({ effectiveDate, questionsObj }) => {
        const { Layup1_From, Layup1_To, Layup2_From, Layup2_To } = questionsObj;
        try {
            // Parse the effective date and calculate the policy end date
            const policyStart = effectiveDate;
            const policyEnd = (0, date_fns_1.addYears)(policyStart, 1);
            if (!(0, date_fns_1.isValid)(policyStart)) {
                throw new Error('Invalid effectiveDate');
            }
            let layupPeriods = [];
            // Function to add layup periods to array if they exist and are valid
            const addLayupPeriod = (from, to) => {
                const startDate = (0, date_fns_1.parseISO)(from);
                const endDate = (0, date_fns_1.parseISO)(to);
                if (!(0, date_fns_1.isValid)(startDate) || !(0, date_fns_1.isValid)(endDate) || startDate >= endDate) {
                    throw new Error('Invalid Layup Dates');
                }
                layupPeriods.push({ start: startDate, end: endDate });
            };
            // Add layup periods
            if (Layup1_From && Layup1_To) {
                addLayupPeriod(Layup1_From, Layup1_To);
            }
            else if (Layup1_From || Layup1_To) {
                throw new Error('Incomplete Layup1 period');
            }
            if (Layup2_From && Layup2_To) {
                addLayupPeriod(Layup2_From, Layup2_To);
            }
            else if (Layup2_From || Layup2_To) {
                throw new Error('Incomplete Layup2 period');
            }
            // Check if layup periods cover the entire policy period
            const earliestStart = Math.min(...layupPeriods.map(period => period.start.getTime()));
            const latestEnd = Math.max(...layupPeriods.map(period => period.end.getTime()));
            if (new Date(earliestStart) <= policyStart && new Date(latestEnd) >= policyEnd) {
                // If layup periods cover the entire policy period, return 12 months
                return 12;
            }
            // Calculate non-overlapping layup days within the policy period
            let totalLayupDays = 0;
            layupPeriods.forEach(period => {
                const start = period.start < policyStart ? policyStart : period.start;
                const end = period.end > policyEnd ? policyEnd : period.end;
                totalLayupDays += start < end ? (0, date_fns_1.differenceInDays)(end, start) : 0;
            });
            // Deduct overlapping days if any
            if (layupPeriods.length > 1) {
                const overlapStart = Math.max(layupPeriods[0].start.getTime(), layupPeriods[1].start.getTime());
                const overlapEnd = Math.min(layupPeriods[0].end.getTime(), layupPeriods[1].end.getTime());
                if (overlapStart < overlapEnd) { // There is an overlap
                    totalLayupDays -= (overlapEnd - overlapStart) / (1000 * 60 * 60 * 24);
                }
            }
            // Convert total days to months for return, using floor to round down
            const totalLayupMonths = Math.floor(totalLayupDays / 30);
            return totalLayupMonths;
        }
        catch {
            return 0;
        }
    };
    const rule = (list = []) => {
        if (!Array.isArray(list))
            return undefined;
        const errors = [];
        // these are the validators from the schema
        const validateEntry = (index, validator, errorPrefix) => {
            if (!validator.validate(list[index])) {
                const error = validator.errors?.[0];
                if (error) {
                    error.message = `${errorPrefix} ${index + 1}: ${error.message}`;
                    error.meta = error.meta || {};
                    error.meta.index = index;
                }
                errors.push(error);
            }
        };
        // this is the custom validator
        const validateTwoMonthsDifference = (index) => {
            if (!validatorFunction.validate(list[index])) {
                const error = {
                    input: list[index],
                    message: (0, locale_1.t)(validatorFunction.errTitle),
                    meta: { index },
                };
                errors.push(error);
            }
        };
        const validateTwoMonthsDifferenceTotal = ({ list }) => {
            if (!validatorFunctionTotal.validate({ list })) {
                const error = {
                    input: list[1],
                    message: (0, locale_1.t)(validatorFunctionTotal.errTitle),
                    meta: { index: 1 },
                };
                errors.push(error);
            }
        };
        // Validate first entry
        validateEntry(0, entryValidator, 'Layup period');
        if (list.length < 2)
            validateTwoMonthsDifference(0);
        // Validate second entry if exists
        if (list.length > 1) {
            validateEntry(0, entryValidator, 'Layup period');
            validateEntry(1, entrySecondValidator, 'Layup period');
            validateTwoMonthsDifferenceTotal({ list });
        }
        return errors.length ? errors[0] : undefined;
    };
    return rule;
};
exports.LayupValidator = LayupValidator;
